import type { Role } from './enums';

/**
 * Permission catalog. UI and API both check permissions, never raw roles.
 * A SUPERVISOR receives a configurable subset; OWNER always has everything.
 */
export const PERMISSIONS = [
  'clients.read',
  'clients.write',
  'properties.read',
  'properties.write',
  'properties.sensitive.read',
  'quotes.read',
  'quotes.write',
  'quotes.approve',
  'services.read',
  'services.manage',
  'jobs.read',
  'jobs.write',
  'jobs.assign',
  'jobs.override',
  'employees.read',
  'employees.manage',
  'teams.read',
  'teams.manage',
  'payroll.read',
  'payroll.approve',
  'payments.read',
  'payments.manage',
  'chat.read',
  'chat.write',
  'chat.assign',
  'finance.read',
  'analytics.read',
  'map.read',
  'settings.read',
  'settings.manage',
  'users.manage',
  'owners.manage',
  'audit.read',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSION_GROUPS: Record<string, readonly Permission[]> = {
  clients: ['clients.read', 'clients.write'],
  properties: ['properties.read', 'properties.write', 'properties.sensitive.read'],
  quotes: ['quotes.read', 'quotes.write', 'quotes.approve'],
  services: ['services.read', 'services.manage'],
  jobs: ['jobs.read', 'jobs.write', 'jobs.assign', 'jobs.override'],
  employees: ['employees.read', 'employees.manage', 'teams.read', 'teams.manage'],
  payroll: ['payroll.read', 'payroll.approve'],
  payments: ['payments.read', 'payments.manage', 'finance.read', 'analytics.read'],
  chat: ['chat.read', 'chat.write', 'chat.assign'],
  admin: ['map.read', 'settings.read', 'settings.manage', 'users.manage', 'owners.manage', 'audit.read'],
};

const ALL: readonly Permission[] = PERMISSIONS;

/** Default permissions for each role. Supervisor defaults are editable per user. */
export const DEFAULT_ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  OWNER: ALL,
  ADMIN: ALL.filter((p) => p !== 'owners.manage'),
  SUPERVISOR: [
    'clients.read',
    'clients.write',
    'properties.read',
    'properties.write',
    'properties.sensitive.read',
    'quotes.read',
    'quotes.write',
    'services.read',
    'jobs.read',
    'jobs.write',
    'jobs.assign',
    'employees.read',
    'teams.read',
    'chat.read',
    'chat.write',
    'chat.assign',
    'map.read',
  ],
  // Employees and clients act on their own data only; access is scoped in services,
  // not via staff permissions.
  EMPLOYEE: [],
  CLIENT: [],
};

export interface PermissionOverrides {
  granted?: readonly string[];
  revoked?: readonly string[];
}

/** Effective permissions = role defaults + per-user grants - per-user revokes. OWNER cannot be restricted. */
export function resolvePermissions(
  role: Role,
  rolePermissions: readonly string[] = DEFAULT_ROLE_PERMISSIONS[role],
  overrides: PermissionOverrides = {},
): Permission[] {
  if (role === 'OWNER') return [...ALL];
  const set = new Set<string>(rolePermissions);
  for (const p of overrides.granted ?? []) set.add(p);
  for (const p of overrides.revoked ?? []) set.delete(p);
  // Only OWNER may manage owners, whatever the overrides say.
  set.delete('owners.manage');
  // Employees and clients can never hold staff permissions.
  if (role === 'EMPLOYEE' || role === 'CLIENT') return [];
  return ALL.filter((p) => set.has(p));
}

export function hasPermission(perms: readonly string[], required: Permission | readonly Permission[]): boolean {
  const list = typeof required === 'string' ? [required] : required;
  return list.every((p) => perms.includes(p));
}

export function hasAnyPermission(perms: readonly string[], required: readonly Permission[]): boolean {
  return required.some((p) => perms.includes(p));
}

export function isPermission(value: string): value is Permission {
  return (PERMISSIONS as readonly string[]).includes(value);
}
